
























import Vue from "vue";
import Component from "vue-class-component";
import { Prop } from "vue-property-decorator";

import { PatientDetailsViewModel, VentSettingModel, VentMode, VentType } from "@/models";
import VentLabel from "./VentLabel.vue";
import VentValue from "./VentValue.vue";
import {
  getVentSettingFieldDefinitions,
  VentFieldInfo,
  BellavistaVentSettingFields,
  DefaultVentSettingFields
} from "./vent-fields";

@Component({
  components: { VentLabel, VentValue }
})
export default class VentSettings extends Vue {
  @Prop() readonly patient!: PatientDetailsViewModel;

  get settings() {
    return this.patient.ventSetting;
  }

  get ventType() {
    return this.settings?.ventTypeKey;
  }

  get isBellavistaType() {
    return this.ventType === VentType.Bellavista;
  }

  get fields(): VentFieldInfo<VentSettingModel>[] {
    let fields: Array<keyof VentSettingModel> = [];

    // Bellavista uses a different set of fields than Avea and only shows fields that have a value
    if (this.isBellavistaType) {
      fields = BellavistaVentSettingFields.filter(f => !!this.settings[f] || this.settings[f] === 0);
    } else {
      fields = this.getAveaFields();
    }

    // Return the field definitions in the same order
    return getVentSettingFieldDefinitions(fields);
  }

  getAveaFields(): Array<keyof VentSettingModel> {
    // Start with these fields which are shown for every mode (except Nasal CPAP modes)
    let fields: Array<keyof VentSettingModel> = ["rate", "peep", "flowTrigger", "fiO2"];

    // FYI SIMV modes are the same as the A/C modes with the addition of the PSV field. Treat them the same, then add
    // PSV afterwards.
    switch (this.settings.mode) {
      case VentMode.VolumeAc:
      case VentMode.VolumeSimv:
        fields = fields.concat(["volume", "flowPeak", "inspirationPause"]);
        break;

      case VentMode.PressureAc:
      case VentMode.PressureSimv:
        fields = fields.concat(["inspirationTime", "inspirationPressure"]);
        break;

      case VentMode.PrvcAc:
      case VentMode.PrvcSimv:
        fields = fields.concat(["inspirationTime", "volume"]);
        break;

      case VentMode.TcplAc:
      case VentMode.TcplSimv:
        fields = fields.concat(["inspirationTime", "inspirationPressure", "flowPeak"]);
        break;

      case VentMode.CpapPsv:
        fields = fields.concat([
          "inspirationTime",
          "volume",
          "inspirationPressure",
          "flowPeak",
          "inspirationPause",
          "psv"
        ]);
        break;

      // Nasal CPAP only shows these fields, so don't concat with the base fields
      case VentMode.NasalCpap:
        fields = ["nCpap", "fiO2"];
        break;

      // Nasal CPAP / IMV only shows these fields, so don't concat with the base fields
      case VentMode.NasalCpapImv:
        fields = ["rate", "inspirationPressure", "inspirationTime", "nCpap", "fiO2"];
        break;

      // If a match isn't found, display all fields like the APRV / BiPhasic mode
      case VentMode.AprvBiPhasic:
      default:
        fields = fields.concat(DefaultVentSettingFields);
    }

    // If the mode is SIMV, it is the same as A/C with the addition of the PSV field, so add the PSV field now
    const simvModes = [VentMode.VolumeSimv, VentMode.PressureSimv, VentMode.PrvcSimv, VentMode.TcplSimv];
    if (simvModes.some(m => m === this.settings.mode)) {
      fields.push("psv");
    }

    return fields;
  }
}
